import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FiFileText,
  FiDownload,
  FiCalendar,
  FiUsers,
  FiTrendingUp,
  FiChevronLeft,
  FiChevronRight,
  FiTrash2,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiChevronDown,
} from "react-icons/fi";
import { FaTimes } from "react-icons/fa";

import {
  Button,
  Loading,
  ChartTitle,
  FormGroup,
  FormLabel,
  FormSelect,
  PageSection,
  PageTitle,
  ReportControls,
  ReportForm,
  Table,
  TableCell,
  TableContainer,
  TableHeader,
  TableRow,
  PageHeader,
  StatsPreview,
  StatCard,
  StatIcon,
  StatValue,
  StatLabel,
  PaginationContainer,
  PaginationButton,
  PaginationInfo,
  StatusBadge,
  Confirm,
  ModalActions,
  Cancel,
  CloseBtn,
  ConfirmBox,
  ModalOverlay,
  Relative,
} from "./AdminReportsPage.styles";

import { formatDate, formatDatePeriod } from "../../../utils/utils";
import AdminContainer from "../../../components/utils/AdminContainer";

// Redux
import {
  getReports,
  generateReport,
  downloadReport,
  deleteReport,
  getReportStats,
} from "../../../store/slices/reports/asyncThunks";
import {
  selectReports,
  selectReportGeneration,
  selectReportDownload,
  selectReportStats,
  clearGenerationError,
} from "../../../store/slices/reports/slice";
import Loader from "../../../components/utils/Loader";
import { selectBandsDropdown } from "../../../store/slices/bands/slice";
import { getBandsForDropdown } from "../../../store/slices/bands/asyncThunks";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "../../../components/modern-ui/sonner";

// Helper functions for months
const getCurrentMonth = () => {
  const now = new Date();
  return {
    month: now.getMonth() + 1, // 1-12
    year: now.getFullYear(),
  };
};

const getMonthName = (month) => {
  const monthNames = [
    "Januar",
    "Februar",
    "Mart",
    "April",
    "Maj",
    "Jun",
    "Jul",
    "Avgust",
    "Septembar",
    "Oktobar",
    "Novembar",
    "Decembar",
  ];
  return monthNames[month - 1];
};

const generateMonthOptions = () => {
  const current = getCurrentMonth();
  const options = [];

  // Generate options for current and previous 12 months
  for (let i = 0; i < 12; i++) {
    let month = current.month - i;
    let year = current.year;

    if (month <= 0) {
      month += 12;
      year -= 1;
    }

    options.push({
      value: `${year}-${month.toString().padStart(2, "0")}`,
      label: `${getMonthName(month)} ${year}`,
    });
  }

  return options;
};

const AdminReportsPage = () => {
  const dispatch = useDispatch();

  // Redux selectors
  const {
    data: reports,
    loading: reportsLoading,
    deleteLoading,
  } = useSelector(selectReports);

  const { loading: generationLoading, error: generationError } = useSelector(
    selectReportGeneration
  );
  const { loading: downloadLoading } = useSelector(selectReportDownload);
  const stats = useSelector(selectReportStats);
  const { data: bandsData } = useSelector(selectBandsDropdown);
  const [deleteModal, setDeleteModal] = useState(false);
  const [reportId, setReportId] = useState(null);

  // Local state
  // Get current month for default selection
  const currentMonth = getCurrentMonth();
  const [formData, setFormData] = useState({
    month: `${currentMonth.year}-${currentMonth.month
      .toString()
      .padStart(2, "0")}`, // Format: "2025-09"
    scope: "all_bands",
    bandId: "",
    format: "pdf",
    title: "",
    description: "",
  });

  // Filters state
  const [filters, setFilters] = useState({
    month: "", // Filter by month instead of type
    scope: "",
    status: "",
    bandId: "",
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [reportsPerPage] = useState(5);

  // Load data on mount and when pagination changes
  useEffect(() => {
    dispatch(
      getReports({
        filters,
        pagination: {
          page: currentPage,
          limit: reportsPerPage,
        },
      })
    );
    dispatch(getReportStats());
    dispatch(getBandsForDropdown());
  }, [dispatch, filters, currentPage, reportsPerPage]);

  // Auto-refresh for pending reports
  useEffect(() => {
    const hasPendingReports = reports.some(
      (report) => report.status === "generating"
    );

    if (hasPendingReports) {
      const interval = setInterval(() => {
        dispatch(
          getReports({
            filters,
            pagination: {
              page: currentPage,
              limit: reportsPerPage,
            },
          })
        );
      }, 3000); // Refresh every 3 seconds

      return () => clearInterval(interval);
    }
  }, [reports, dispatch, filters, currentPage, reportsPerPage]);

  // Form handlers
  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Reset to first page when filters change
    setCurrentPage(1);
  };

  // Generate report
  const handleGenerateReport = async () => {
    if (!formData.month) {
      toast.warning("Molimo izaberite mesec za izveštaj");

      return;
    }

    if (formData.scope === "single_band" && !formData.bandId) {
      toast.warning("Molimo izaberite bend za pojedinačni izveštaj");

      return;
    }

    // Validacija da mesec nije u budućnosti
    const [year, monthNum] = formData.month.split("-");
    const requestedDate = new Date(year, monthNum - 1);
    const currentDate = new Date();
    currentDate.setDate(1);

    if (requestedDate > currentDate) {
      toast.error("Nije moguće generirati izveštaj za budući mesec");

      return;
    }

    const reportData = {
      month: formData.month,
      scope: formData.scope,
      format: formData.format,
    };

    if (formData.scope === "single_band" && formData.bandId) {
      reportData.bandId = parseInt(formData.bandId);
    }

    // Dodaj title i description ako nisu prazni
    if (formData.title && formData.title.trim()) {
      reportData.title = formData.title.trim();
    }
    if (formData.description && formData.description.trim()) {
      reportData.description = formData.description.trim();
    }

    try {
      await dispatch(generateReport(reportData)).unwrap();

      // Reset form
      const resetMonth = getCurrentMonth();
      setFormData({
        month: `${resetMonth.year}-${resetMonth.month
          .toString()
          .padStart(2, "0")}`,
        scope: "all_bands",
        bandId: "",
        format: "pdf",
        title: "",
        description: "",
      });

      // Refresh reports lista
      dispatch(getReports(filters));
      dispatch(getReportStats());
    } catch (error) {
      toast.error(error.message);

      // Detaljnije error handling
      let errorMessage = "Greška pri generiranju izveštaja";

      if (error.message) {
        errorMessage = error.message;
      } else if (error.error) {
        errorMessage = error.error;
      } else if (typeof error === "string") {
        errorMessage = error;
      }
    }
  };

  // Download report
  const handleDownloadReport = async (reportId) => {
    try {
      const result = await dispatch(downloadReport(reportId)).unwrap();

      if (result && result.success) {
        toast.success(
          `Izveštaj ${reportId} je uspešno preuzet:`,
          result.filename
        );
      }
    } catch (error) {
      toast.error(error.message);

      let errorMessage = "Greška pri preuzimanju izveštaja";

      if (error.message) {
        errorMessage = error.message;
      } else if (error.error) {
        errorMessage = error.error;
      }
    }
  };

  // Get pagination info from Redux
  const reportsInfo = useSelector(selectReports);
  const { pagination } = reportsInfo;
  const totalPages = pagination?.totalPages || 0;

  // Pagination handlers
  const goToPage = (page) => setCurrentPage(page);
  const goToPreviousPage = () => {
    if (pagination?.hasPreviousPage) {
      setCurrentPage(currentPage - 1);
    }
  };
  const goToNextPage = () => {
    if (pagination?.hasNextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Since we're using server-side pagination, no need to slice reports
  const currentReports = reports;

  // Status badge component
  const StatusBadgeComponent = ({ status }) => {
    const getStatusInfo = (status) => {
      switch (status) {
        case "completed":
          return { icon: <FiCheckCircle />, color: "#27ae60", text: "Završen" };
        case "generating":
          return { icon: <FiClock />, color: "#f39c12", text: "Generiše se" };
        case "failed":
          return { icon: <FiXCircle />, color: "#e74c3c", text: "Neuspešan" };
        default:
          return { icon: <FiClock />, color: "#95a5a6", text: "Nepoznat" };
      }
    };

    const statusInfo = getStatusInfo(status);

    return (
      <StatusBadge $color={statusInfo.color}>
        {statusInfo.icon}
        {statusInfo.text}
      </StatusBadge>
    );
  };

  return (
    <>
      <AdminContainer>
        <PageSection>
          <PageHeader>
            <PageTitle>Mesečni Izveštaji</PageTitle>
          </PageHeader>

          {/* Statistics */}
          {stats && (
            <StatsPreview>
              <StatCard>
                <StatIcon>
                  <FiTrendingUp />
                </StatIcon>
                <StatValue>{stats.totalReports}</StatValue>
                <StatLabel>Ukupno Izveštaja</StatLabel>
              </StatCard>

              <StatCard>
                <StatIcon>
                  <FiCheckCircle />
                </StatIcon>
                <StatValue>{stats.completedReports}</StatValue>
                <StatLabel>Završeni</StatLabel>
              </StatCard>

              <StatCard>
                <StatIcon>
                  <FiClock />
                </StatIcon>
                <StatValue>{stats.pendingReports}</StatValue>
                <StatLabel>U toku</StatLabel>
              </StatCard>

              <StatCard>
                <StatIcon>
                  <FiDownload />
                </StatIcon>
                <StatValue>{stats.totalDownloads}</StatValue>
                <StatLabel>Preuzimanja</StatLabel>
              </StatCard>
            </StatsPreview>
          )}

          {/* Report Generation Form */}
          <ReportControls>
            <ReportForm>
              <FormGroup>
                <FormLabel>
                  <FiCalendar />
                  Izaberite Mesec
                </FormLabel>
                <Relative>
                  <FormSelect
                    value={formData.month}
                    onChange={(e) => handleFormChange("month", e.target.value)}
                  >
                    {generateMonthOptions().map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </FormSelect>
                  <FiChevronDown
                    color="#ff6b35"
                    size={20}
                    style={{
                      position: "absolute",
                      right: 15,
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  />
                </Relative>
              </FormGroup>

              <FormGroup>
                <FormLabel>
                  <FiUsers />
                  Obuhvat
                </FormLabel>
                <Relative>
                  <FormSelect
                    value={formData.scope}
                    onChange={(e) => handleFormChange("scope", e.target.value)}
                  >
                    <option value="all_bands">Svi bendovi</option>
                    <option value="single_band">Pojedinačni bend</option>
                  </FormSelect>
                  <FiChevronDown
                    color="#ff6b35"
                    size={20}
                    style={{
                      position: "absolute",
                      right: 15,
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  />
                </Relative>
              </FormGroup>

              {formData.scope === "single_band" && (
                <FormGroup>
                  <FormLabel>Bend</FormLabel>
                  <Relative>
                    <FormSelect
                      value={formData.bandId}
                      onChange={(e) =>
                        handleFormChange("bandId", e.target.value)
                      }
                    >
                      <option value="">Izaberite bend</option>
                      {bandsData &&
                        bandsData.map((band) => (
                          <option key={band.id} value={band.id}>
                            {band?.name}
                          </option>
                        ))}
                    </FormSelect>
                    <FiChevronDown
                      color="#ff6b35"
                      size={20}
                      style={{
                        position: "absolute",
                        right: 15,
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    />
                  </Relative>
                </FormGroup>
              )}

              <FormGroup>
                <FormLabel>Format</FormLabel>
                <Relative>
                  <FormSelect
                    value={formData.format}
                    onChange={(e) => handleFormChange("format", e.target.value)}
                  >
                    <option value="pdf">PDF</option>
                    <option value="excel">Excel</option>
                  </FormSelect>
                  <FiChevronDown
                    color="#ff6b35"
                    size={20}
                    style={{
                      position: "absolute",
                      right: 15,
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  />
                </Relative>
              </FormGroup>

              <FormGroup>
                <FormLabel style={{ opacity: 0 }}>Dugme</FormLabel>
                <Button
                  style={{ height: 44.59, width: "fit-content" }}
                  $variant="primary"
                  onClick={handleGenerateReport}
                  disabled={generationLoading}
                >
                  {generationLoading ? (
                    <>
                      <Loading />
                      Generiše se...
                    </>
                  ) : (
                    <>
                      <FiFileText size={16} />
                      Generiši Izveštaj
                    </>
                  )}
                </Button>
              </FormGroup>
            </ReportForm>
          </ReportControls>

          {/* Error handling */}
          {generationError && (
            <div style={{ color: "#e74c3c", margin: "1rem 0" }}>
              Greška pri generiranju: {generationError}
              <Button
                $variant="secondary"
                onClick={() => dispatch(clearGenerationError())}
                style={{ marginLeft: "1rem" }}
              >
                Zatvori
              </Button>
            </div>
          )}

          {/* Filters */}
          <ReportControls>
            <h4 style={{ color: "#ff6b35", marginBottom: "1rem" }}>Filteri</h4>
            <ReportForm>
              <FormGroup>
                <FormLabel>Mesec</FormLabel>
                <Relative>
                  <FormSelect
                    value={filters.month}
                    onChange={(e) =>
                      handleFilterChange("month", e.target.value)
                    }
                  >
                    <option value="">Svi meseci</option>
                    {generateMonthOptions().map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </FormSelect>
                  <FiChevronDown
                    color="#ff6b35"
                    size={20}
                    style={{
                      position: "absolute",
                      right: 15,
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  />
                </Relative>
              </FormGroup>

              <FormGroup>
                <FormLabel>Status</FormLabel>
                <Relative>
                  <FormSelect
                    value={filters.status}
                    onChange={(e) =>
                      handleFilterChange("status", e.target.value)
                    }
                  >
                    <option value="">Svi statusi</option>
                    <option value="completed">Završen</option>
                    <option value="generating">Generiše se</option>
                    <option value="failed">Neuspešan</option>
                  </FormSelect>
                  <FiChevronDown
                    color="#ff6b35"
                    size={20}
                    style={{
                      position: "absolute",
                      right: 15,
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  />
                </Relative>
              </FormGroup>

              <FormGroup>
                <FormLabel>Obuhvat</FormLabel>
                <Relative>
                  <FormSelect
                    value={filters.scope}
                    onChange={(e) =>
                      handleFilterChange("scope", e.target.value)
                    }
                  >
                    <option value="">Svi</option>
                    <option value="all_bands">Svi bendovi</option>
                    <option value="single_band">Pojedinačni bend</option>
                  </FormSelect>
                  <FiChevronDown
                    color="#ff6b35"
                    size={20}
                    style={{
                      position: "absolute",
                      right: 15,
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  />
                </Relative>
              </FormGroup>
            </ReportForm>
          </ReportControls>

          {/* Reports Table */}
          <TableContainer>
            <ChartTitle>Izveštaji</ChartTitle>
            <Table>
              <thead>
                <tr>
                  <TableHeader>Naslov</TableHeader>
                  <TableHeader>Period</TableHeader>
                  <TableHeader>Obuhvat</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Format</TableHeader>
                  <TableHeader>Veličina</TableHeader>
                  <TableHeader>Kreiran</TableHeader>
                  <TableHeader>Akcije</TableHeader>
                </tr>
              </thead>
              <tbody>
                {reportsLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      style={{ textAlign: "center", padding: "2rem" }}
                    >
                      <Loader title={"izveštaja"} />
                    </TableCell>
                  </TableRow>
                ) : currentReports.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      style={{
                        textAlign: "center",
                        padding: "2rem",
                        color: "#cccccc",
                      }}
                    >
                      <div
                        style={{ fontSize: "1.2rem", marginBottom: "0.5rem" }}
                      >
                        <FiFileText size={32} color="#666" />
                      </div>
                      <div>Trenutno nema mesečnih izveštaja</div>
                      <div
                        style={{
                          fontSize: "0.9rem",
                          marginTop: "0.5rem",
                          opacity: 0.7,
                        }}
                      >
                        Novi izveštaji će se prikazati ovde
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  currentReports.map((report) => (
                    <TableRow key={report.id}>
                      <TableCell>
                        {report.bandId
                          ? "Mesečni izveštaj " +
                            (report.band?.name || "nepoznat bend")
                          : "Celokupni mesečni izveštaj"}
                      </TableCell>
                      <TableCell>
                        {formatDatePeriod(report.periodStart, report.periodEnd)}
                      </TableCell>

                      <TableCell>
                        {report.scope === "all_bands"
                          ? "Svi bendovi"
                          : "Pojedinačni"}
                      </TableCell>
                      <TableCell>
                        <StatusBadgeComponent status={report.status} />
                      </TableCell>
                      <TableCell>{report.format?.toUpperCase()}</TableCell>
                      <TableCell>
                        {report.fileSizeMB ? `${report.fileSizeMB} MB` : "-"}
                      </TableCell>
                      <TableCell>{formatDate(report.createdAt)}</TableCell>
                      <TableCell>
                        <div
                          style={{
                            display: "flex",
                            gap: "0.5rem",
                            justifyContent: "flex-end",
                          }}
                        >
                          {report.status === "completed" && (
                            <Button
                              $variant="secondary"
                              onClick={() => handleDownloadReport(report.id)}
                              disabled={downloadLoading}
                            >
                              <FiDownload size={16} />
                              Preuzmi
                            </Button>
                          )}
                          <Button
                            $variant="danger"
                            onClick={() => {
                              setReportId(report.id);
                              setDeleteModal(true);
                            }}
                            disabled={deleteLoading}
                          >
                            <FiTrash2 size={16} />
                            Obriši
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </tbody>
            </Table>
            {/* Pagination */}
            <PaginationContainer>
              <PaginationInfo>
                Prikazuje se {currentReports?.length} od{" "}
                {pagination?.totalItems} izveštaja
              </PaginationInfo>

              <div>
                <PaginationButton
                  onClick={goToPreviousPage}
                  disabled={!pagination?.hasPreviousPage}
                >
                  <FiChevronLeft />
                </PaginationButton>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <PaginationButton
                      key={page}
                      onClick={() => goToPage(page)}
                      $active={currentPage === page}
                    >
                      {page}
                    </PaginationButton>
                  )
                )}

                <PaginationButton
                  onClick={goToNextPage}
                  disabled={!pagination?.hasNextPage}
                >
                  <FiChevronRight />
                </PaginationButton>
              </div>
            </PaginationContainer>
          </TableContainer>
        </PageSection>
      </AdminContainer>
      <AnimatePresence>
        {deleteModal && (
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              if (!deleteLoading) {
                setDeleteModal(false);
              }
            }}
          >
            <ConfirmBox
              as={motion.div}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseBtn
                onClick={() => {
                  if (!deleteLoading) {
                    setDeleteModal(false);
                  }
                }}
              >
                <FaTimes size={16} />
              </CloseBtn>
              <h3>Potvrdi brisanje</h3>
              <p>Da li si siguran da želiš da obrišeš ovaj izveštaj?</p>
              <ModalActions>
                <Cancel
                  onClick={() => {
                    if (!deleteLoading) {
                      setDeleteModal(false);
                    }
                  }}
                  disabled={deleteLoading}
                >
                  Otkaži
                </Cancel>
                <Confirm
                  onClick={() => {
                    dispatch(deleteReport(reportId))
                      .unwrap()
                      .then(() => {
                        setDeleteModal(false);
                        toast.success("Uspešno ste obrisali izveštaj.");
                      })
                      .catch((err) => toast.error(err.message));
                  }}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? "Brišem..." : "Obriši"}
                </Confirm>
              </ModalActions>
            </ConfirmBox>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </>
  );
};

export default AdminReportsPage;
